"use client";
import type { ReactNode } from "react";
import type { SurveyQuestion } from "@/types/survey";
import { motion } from "motion/react";
import { getRatingConfig } from "./ratingDefaults";

interface NPSRatingQuestionProps {
  question: SurveyQuestion;
  brandColor?: string;
  isSelected?: (
    question: SurveyQuestion,
    candidate: string | number,
  ) => boolean;
  onAnswerChange?: (question: SurveyQuestion, value: string) => void;
  isPreview?: boolean;
  selectedValue?: string | number;
  onPreviewAnswerChange?: (questionId: string, value: string | number) => void;
  uiStrings?: {
    npsLowLabel: string;
    npsHighLabel: string;
  };
}

const motionTransition = { type: "spring" as const, stiffness: 400, damping: 17, duration: 0.2 };

export default function NPSRatingQuestion({
  question,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  brandColor = "var(--color-alias-info)",
  isSelected,
  onAnswerChange,
  isPreview = false,
  selectedValue,
  onPreviewAnswerChange,
  uiStrings,
}: NPSRatingQuestionProps) {
  const { lowLabel, highLabel, backgroundColor, textColor: textColorOpt, borderColor } =
    getRatingConfig(question, {
      lowLabel: uiStrings?.npsLowLabel,
      highLabel: uiStrings?.npsHighLabel,
    });
  const textColor = textColorOpt ?? "var(--color-alias-foreground-on-color)";
  // Selection ring stays at 2px in textColor; the optional 1px borderColor
  // only applies in the unselected state.
  const baseBorder = borderColor ? `1px solid ${borderColor}` : "2px solid transparent";
  const selectedBorder = `2px solid ${textColor}`;

  const bandedColor = (i: number) =>
    i <= 3
      ? "var(--color-alias-error-light)"
      : i <= 6
        ? "var(--color-alias-warning-light)"
        : i <= 8
          ? "var(--color-alias-success-light)"
          : "var(--color-alias-success-dark)";

  const handleAnswerChange = (value: string | number) => {
    if (isPreview && onPreviewAnswerChange) {
      onPreviewAnswerChange(question.id, value);
    } else if (onAnswerChange) {
      onAnswerChange(question, value.toString());
    }
  };

  const isItemSelected = (value: number) => {
    if (isPreview) {
      return selectedValue === value;
    }
    return isSelected ? isSelected(question, value) : false;
  };

  const itemStyle = (i: number) => ({
    color: textColor,
    border: isItemSelected(i) ? selectedBorder : baseBorder,
    backgroundColor: backgroundColor ?? bandedColor(i),
    boxShadow: isItemSelected(i) ? "var(--box-shadow-md)" : "none",
  });
  const motionProps = (i: number) => ({
    animate: { filter: isItemSelected(i) ? "brightness(1.2)" : "brightness(1)" },
    whileHover: { scale: 1.1, boxShadow: "var(--box-shadow-md)" },
    whileTap: { scale: 0.95 },
    transition: motionTransition,
  });

  const renderPreviewItem = (i: number) => (
    <motion.button
      key={i}
      onClick={() => handleAnswerChange(i)}
      className="w-9 h-9 rounded-md font-semibold"
      style={itemStyle(i)}
      {...motionProps(i)}
    >
      {i}
    </motion.button>
  );

  const renderInteractiveItem = (i: number) => (
    <div key={i}>
      <input
        type="radio"
        id={`rating-${question.id}-${i}`}
        value={i}
        name={`rating-${question.id}`}
        className="sr-only"
        checked={isItemSelected(i)}
        onChange={() => handleAnswerChange(i)}
      />
      <motion.label
        htmlFor={`rating-${question.id}-${i}`}
        className="w-9 h-9 rounded-md font-semibold flex items-center justify-center cursor-pointer"
        style={itemStyle(i)}
        {...motionProps(i)}
      >
        {i}
      </motion.label>
    </div>
  );

  // Wrapper sizes to its widest child (the buttons row) so the labels row's
  // `w-full` matches that exact width — keeping the low/high labels aligned
  // with the first and last rating buttons, not the question container edges.
  const renderRow = (renderItem: (i: number) => ReactNode) => (
    <div className="flex justify-center">
      <div className="inline-flex flex-col max-w-full">
        <div className="flex flex-wrap items-center justify-center gap-2">
          {[...Array(11)].map((_, i) => renderItem(i))}
        </div>
        <div className="mt-1 text-[11px] flex justify-between w-full" style={{ color: "var(--sl-text-muted, var(--color-alias-neutral-dark))" }}>
          <span>{lowLabel}</span>
          <span>{highLabel}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mb-6">
      <div className="text-sm font-medium mb-2 text-center" style={{ color: "var(--sl-text, var(--color-alias-neutral-darkest))" }}>
        {question.text}
      </div>
      {renderRow(isPreview ? renderPreviewItem : renderInteractiveItem)}
    </div>
  );
}
